import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** True when the entrance may run: IntersectionObserver exists and motion is allowed. */
export function canReveal(win = typeof window === 'undefined' ? undefined : window) {
  if (!win || typeof win.IntersectionObserver !== 'function') return false;
  if (typeof win.matchMedia === 'function') {
    try {
      if (win.matchMedia(REDUCED_MOTION_QUERY).matches) return false;
    } catch (e) {
      return false;
    }
  }
  return true;
}

// One shared, passive scroll / resize / hashchange listener for every armed Reveal (a safety
// net, owner upgrade brief 2026-09-27 §2.5): an element scrolled past without the observer
// seeing it (a fast fling, a jump) is finished at once, and a jump to a #hash finishes all.
const armed = new Set();
let frame = 0;
let listening = false;
const runChecks = (force) => {
  frame = 0;
  armed.forEach((check) => check(force));
};
const onScroll = () => {
  if (!frame) frame = window.requestAnimationFrame(() => runChecks(false));
};
const onHash = () => runChecks(true);
function watch(check) {
  armed.add(check);
  if (!listening) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('hashchange', onHash);
    listening = true;
  }
  return () => {
    armed.delete(check);
    if (!armed.size && listening) {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('hashchange', onHash);
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      listening = false;
    }
  };
}

// idle: plain, fully visible content (the default, and all it ever is without
// IntersectionObserver, under reduced motion, or when it is already in view at mount).
// armed: below the fold, waiting at the animation's start pose (30% opacity, 15px low).
// play: the 600ms rise (animate-site-rise), once.
const STATE_CLASSES = {
  idle: '',
  armed: 'translate-y-[15px] opacity-30',
  play: 'animate-site-rise',
};

/**
 * Section entrance: content rises 15px into place (600ms) the first time it scrolls
 * into view, as in the reference. Content is never hidden: it renders fully visible
 * by default and stays that way without JavaScript, without IntersectionObserver,
 * under prefers-reduced-motion (checked at mount, and the classes also carry
 * motion-reduce overrides), in print, and when it is already on screen at mount (so a
 * hero never waits on it — do not wrap the page's LCP content anyway). The observer
 * is disconnected after the first reveal and on unmount.
 *
 * Only content BELOW the screen is armed. Upgrade brief 2026-09-27 §2.5: a page opened on a
 * #hash never arms anything (the jump lands on finished content); a later hash jump finishes
 * every armed element at once; anything scrolled past without being seen (above the screen)
 * is finished without the animation, so nothing stays faded behind the reader.
 *
 * Props:
 * - `as`: element (default 'div')
 * - `threshold`: IntersectionObserver threshold (default 0.13, the reference's)
 * - `className`, `children`, other props pass through (it carries data-reveal="idle|armed|play").
 */
export default function Reveal({ as: Tag = 'div', threshold = 0.13, className, children, ...rest }) {
  const ref = useRef(null);
  const [state, setState] = useState('idle');

  useEffect(() => {
    const el = ref.current;
    if (!el || !canReveal()) return undefined;
    if (window.location && window.location.hash) return undefined; // a jump: finished content
    const rect = el.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight || 0;
    if (rect.top < viewport) return undefined; // on screen or above it: leave it plain
    setState('armed');
    let unwatch = () => {};
    const io = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setState('play');
          io.disconnect();
          unwatch();
        }
      },
      { threshold },
    );
    io.observe(el);
    unwatch = watch((force) => {
      if (force || el.getBoundingClientRect().bottom <= 0) {
        setState('idle');
        io.disconnect();
        unwatch();
      }
    });
    return () => {
      io.disconnect();
      unwatch();
    };
  }, [threshold]);

  return (
    <Tag
      ref={ref}
      data-reveal={state}
      className={cn(
        STATE_CLASSES[state],
        state !== 'idle' && 'motion-reduce:translate-y-0 motion-reduce:animate-none motion-reduce:opacity-100 print:translate-y-0 print:animate-none print:opacity-100',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
